import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Collapse,
  Checkbox,
  Switch,
} from "antd";
import {
  createProfileMutation,
  patchProfileMutation,
  listProfilesQueryKey,
} from "../../../services/client/@tanstack/react-query.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { DurationPicker } from "../../common/DurationPicker";
import { GetProfileDto } from "../../../services/client";
import { unreachable } from "../../../util/unreachable";
import { useIsViewer } from "../../../hooks/useIsViewer";
import { DisableForViewer } from "../../common/DisableForViewer";

type ProfileFormProps = {
  site_id: string;
  onSuccess?: () => void;
} & (
  | {
      mode: "create";
      profile?: undefined;
    }
  | {
      mode: "update";
      profile: GetProfileDto;
    }
);

export const ProfileForm: React.FC<ProfileFormProps> = ({
  site_id,
  mode,
  profile,
  onSuccess,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const isViewer = useIsViewer();
  // Tri-state: when unchecked, allow_zero_current is left unset (null).
  const allowZeroCurrentEnabled = Form.useWatch(
    "allow_zero_current_enabled",
    form,
  );

  const createMutation = useMutation({
    ...createProfileMutation(),
    onSuccess: () => {
      form.resetFields();
      queryClient.invalidateQueries({
        queryKey: listProfilesQueryKey({ path: { site_id } }),
      });
      notify({
        type: "success",
        message: "Profile Created",
        description: "The profile has been successfully created.",
      });
      onSuccess?.();
    },
  });

  const updateMutation = useMutation({
    ...patchProfileMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listProfilesQueryKey({ path: { site_id } }),
      });
      notify({
        type: "success",
        message: "Profile Updated",
        description: "The profile has been successfully updated.",
      });
      onSuccess?.();
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: any) => {
    switch (mode) {
      case "create":
        createMutation.mutate({
          body: {
            profile_id: values.profile_id,
            description: values.description,
            ac: {
              default_estimated_departure_time_delta:
                values.default_estimated_departure_time_delta,
              default_requested_min_energy: values.default_requested_min_energy,
              default_requested_max_energy: values.default_requested_max_energy,
              default_minimal_charging_current:
                values.default_minimal_charging_current ?? null,
              default_allow_zero_current: values.allow_zero_current_enabled
                ? (values.default_allow_zero_current ?? false)
                : null,
            },
            override_transaction_input: values.override_transaction_input,
          },
          path: { site_id },
        });
        break;
      case "update":
        updateMutation.mutate({
          path: {
            site_id,
            profile_id: profile.profile_id,
          },
          body: {
            description: values.description,
            ac: {
              default_estimated_departure_time_delta:
                values.default_estimated_departure_time_delta,
              default_requested_min_energy: values.default_requested_min_energy,
              default_requested_max_energy: values.default_requested_max_energy,
              default_minimal_charging_current:
                values.default_minimal_charging_current ?? null,
              default_allow_zero_current: values.allow_zero_current_enabled
                ? (values.default_allow_zero_current ?? false)
                : null,
            },
            override_transaction_input: values.override_transaction_input,
          },
        });
        break;
      default:
        unreachable(mode);
    }
  };

  const minEnergyRules = [
    { required: true, message: "Please input minimum energy" },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      validator: (_: any, minValue: number) => {
        const maxValue = form.getFieldValue("default_requested_max_energy");
        if (maxValue === undefined || minValue <= maxValue) {
          return Promise.resolve();
        }
        return Promise.reject(
          new Error("Min energy must be less than or equal to Max energy."),
        );
      },
    },
  ];

  const maxEnergyRules = [
    { required: true, message: "Please input maximum energy" },
  ];

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Form form={form} layout="vertical" disabled={isViewer} onFinish={onFinish}>
      <Form.Item
        name="profile_id"
        label="Profile identifier"
        initialValue={profile?.profile_id}
        rules={
          mode === "create"
            ? [
                {
                  required: true,
                  message: "Please input a unique profile identifier",
                },
              ]
            : []
        }
      >
        <Input disabled={mode === "update"} />
      </Form.Item>
      <Form.Item
        name="description"
        label="Description"
        initialValue={profile?.description}
      >
        <Input.TextArea rows={2} />
      </Form.Item>
      <Form.Item
        name="default_estimated_departure_time_delta"
        label="The amount of time expected before the vehicle departs."
        initialValue={profile?.ac?.default_estimated_departure_time_delta}
        rules={[
          {
            required: true,
            message: "Please input a valid ISO 8601 duration",
          },
          {
            pattern:
              /^P(?:\d+Y)?(?:\d+M)?(?:\d+D)?(?:T(?:\d+H)?(?:\d+M)?(?:\d+S)?)?$/,
            message: "Duration must follow ISO 8601 (e.g. P1DT2H30M)",
          },
        ]}
      >
        <DurationPicker />
      </Form.Item>
      <Form.Item
        name="default_requested_min_energy"
        label="Default Requested Min Energy (Wh)"
        initialValue={profile?.ac?.default_requested_min_energy}
        rules={minEnergyRules}
      >
        <InputNumber className="w-full" addonAfter="Wh" />
      </Form.Item>
      <Form.Item
        name="default_requested_max_energy"
        label="Default Requested Max Energy (Wh)"
        initialValue={profile?.ac?.default_requested_max_energy}
        rules={maxEnergyRules}
      >
        <InputNumber className="w-full" addonAfter="Wh" />
      </Form.Item>
      <Form.Item
        name="default_minimal_charging_current"
        label="Default Minimal Charging Current (A)"
        initialValue={
          profile?.ac?.default_minimal_charging_current ?? undefined
        }
        extra="Site/hardware minimum current. Leave empty to use the per-session value."
      >
        <InputNumber className="w-full" addonAfter="A" min={0} step={0.1} />
      </Form.Item>
      <Form.Item
        name="allow_zero_current_enabled"
        valuePropName="checked"
        initialValue={profile?.ac?.default_allow_zero_current != null}
        extra="Override whether sessions may pause to zero current."
      >
        <Checkbox>Set zero-current policy</Checkbox>
      </Form.Item>
      {allowZeroCurrentEnabled && (
        <Form.Item
          name="default_allow_zero_current"
          label="Allow zero current"
          valuePropName="checked"
          initialValue={profile?.ac?.default_allow_zero_current ?? true}
        >
          <Switch />
        </Form.Item>
      )}
      <Collapse className="mb-4">
        <Collapse.Panel header="Advanced" key="advanced">
          <Form.Item
            name="override_transaction_input"
            valuePropName="checked"
            initialValue={profile?.override_transaction_input ?? false}
          >
            <Checkbox>Override transaction input</Checkbox>
          </Form.Item>
          <p className="text-sm text-gray-500 -mt-4 mb-4">
            This will override the estimated departure time and requested energy
            from the local controller. If the controller has any driver app
            integration, enabling this may lead to driver requests being ignored
            in favor of the profile defaults.
          </p>
        </Collapse.Panel>
      </Collapse>
      <Form.Item>
        <DisableForViewer>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {mode === "create" ? "Create Profile" : "Update Profile"}
          </Button>
        </DisableForViewer>
      </Form.Item>
    </Form>
  );
};
